import { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera,
  Heart,
  Image,
  Images,
  LoaderCircle,
  Lock,
  LogOut,
  RefreshCw,
  RotateCcw,
  ScanLine,
  Search,
  Share2,
  Upload,
  User,
  X,
} from "lucide-react";
import {
  abortPhotoUpload,
  faceSearch,
  hydrateFaceMatches,
  loadFaceSearchReadiness,
  joinEvent,
  loadGalleryPage,
  loadGuestUploadPolicy,
  loadWorkspace,
  requestOtp,
  resolveEventAccess,
  retryPhotoProcessing,
  setFavorite,
  setSharing,
  signOut,
  uploadPhoto,
  validateGuestFile,
  verifyOtp,
} from "./api";
import { capturePendingQrContext, clearPendingQrContext } from "./qr-context";
import { captureOtpFill, showDevOtpFill } from "./otp-fill";
import { getGuestViewFromUrl, navigateGuestView } from "./routes";
import "./dynamic.css";

const nav = [
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "find", label: "Find me", icon: Search },
  { id: "mine", label: "My photos", icon: Camera },
  { id: "profile", label: "Profile", icon: User },
];

const emptyFaceSearchReadiness = {
  sharedPhotoCount: 0,
  indexedPhotoCount: 0,
  pendingPhotoCount: 0,
  failedPhotoCount: 0,
  ready: false,
};

function App() {
  const [otpFill, setOtpFill] = useState(() => captureOtpFill("user"));
  const [qrContext] = useState(() => capturePendingQrContext());
  const [view, setViewState] = useState(() => getGuestViewFromUrl());
  const [workspace, setWorkspace] = useState(null);
  const [eventId, setEventId] = useState("");
  const galleryEventRef = useRef("");
  useEffect(() => {
    galleryEventRef.current = eventId;
    currentFaceSearchEvent.current = eventId;
    setMatches([]);
    setFaceSearchCompleted(false);
  }, [eventId]);
  const [photos, setPhotos] = useState([]);
  const [mine, setMine] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryPages, setGalleryPages] = useState(1);
  const [minePage, setMinePage] = useState(1);
  const [minePages, setMinePages] = useState(1);
  const [moreBusy, setMoreBusy] = useState("");
  const [matches, setMatches] = useState([]);
  const [faceSearchCompleted, setFaceSearchCompleted] = useState(false);
  const [faceSearchReadiness, setFaceSearchReadiness] = useState(
    emptyFaceSearchReadiness,
  );
  const [faceSearchReadinessLoading, setFaceSearchReadinessLoading] =
    useState(false);
  const currentFaceSearchEvent = useRef("");
  const [uploadPolicy, setUploadPolicy] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const uploadControllers = useRef(new Map());
  const [access, setAccess] = useState(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [otpEmail, setOtpEmail] = useState(otpFill?.email || "");
  const [devCode, setDevCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [error, setError] = useState(qrContext.error || "");
  const [accessToken, setAccessToken] = useState(qrContext.token || "");

  const setView = useCallback((nextView) => {
    setViewState(nextView);
    navigateGuestView(nextView);
  }, []);

  useEffect(() => {
    function handleOtpHash() {
      const fill = captureOtpFill("user");
      if (fill) {
        setOtpFill(fill);
        setOtpEmail(fill.email);
        setAuthRequired(true);
      }
    }
    window.addEventListener("hashchange", handleOtpHash);
    window.addEventListener("focus", handleOtpHash);
    return () => {
      window.removeEventListener("hashchange", handleOtpHash);
      window.removeEventListener("focus", handleOtpHash);
    };
  }, []);

  useEffect(() => {
    function handlePop() {
      setViewState(getGuestViewFromUrl());
    }
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let joinedEventId = "";
      if (accessToken) {
        const resolved = await resolveEventAccess(accessToken);
        setAccess(resolved);
        try {
          const joined = await joinEvent(accessToken);
          joinedEventId = joined.eventId;
          clearPendingQrContext();
          setAccessToken("");
        } catch (requestError) {
          if (requestError.status === 401) {
            setWorkspace(null);
            setAuthRequired(true);
            return;
          }
          throw requestError;
        }
      }
      const data = await loadWorkspace();
      setWorkspace(data);
      setAuthRequired(false);
      setEventId(
        (current) =>
          joinedEventId ||
          (data.events.some((event) => event.id === current)
            ? current
            : data.events[0]?.id || ""),
      );
    } catch (requestError) {
      setWorkspace(null);
      setAuthRequired(requestError.status === 401);
      setError(
        requestError.code === "EMAIL_VERIFICATION_REQUIRED"
          ? "Verify your email address, then return to this QR link to join the event."
          : requestError.message,
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  const refreshPhotos = useCallback(async () => {
    if (!eventId) {
      setPhotos([]);
      setMine([]);
      return;
    }
    try {
      const requestedEvent = eventId;
      const [visible, own] = await Promise.all([
        loadGalleryPage(eventId, "shared"),
        loadGalleryPage(eventId, "mine"),
      ]);
      if (galleryEventRef.current !== requestedEvent) return;
      setPhotos(visible.photos);
      setMine(own.photos);
      setGalleryPage(1);
      setGalleryPages(visible.pagination.pages);
      setMinePage(1);
      setMinePages(own.pagination.pages);
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [eventId]);
  useEffect(() => {
    refreshPhotos();
  }, [refreshPhotos]);
  useEffect(() => {
    const controller = new AbortController();
    let timer;
    let stopped = false;
    if (!eventId || view !== "find") {
      if (!eventId) setFaceSearchReadiness(emptyFaceSearchReadiness);
      return () => controller.abort();
    }
    setFaceSearchReadinessLoading(true);
    async function pollReadiness() {
      try {
        const readiness = await loadFaceSearchReadiness(
          eventId,
          controller.signal,
        );
        if (stopped) return;
        setFaceSearchReadiness(readiness);
        setFaceSearchReadinessLoading(false);
        if (readiness.pendingPhotoCount > 0) {
          timer = setTimeout(pollReadiness, 2000);
        }
      } catch (requestError) {
        if (requestError.name !== "AbortError" && !stopped) {
          setFaceSearchReadinessLoading(false);
          setError(requestError.message);
        }
      }
    }
    pollReadiness();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [eventId, view]);
  useEffect(() => {
    const controller = new AbortController();
    if (!eventId) {
      setUploadPolicy(null);
      return () => controller.abort();
    }
    loadGuestUploadPolicy(eventId, controller.signal)
      .then(setUploadPolicy)
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      });
    return () => controller.abort();
  }, [eventId]);

  async function sendCode(email) {
    setBusy(true);
    setError("");
    setDevCode("");
    try {
      const result = await requestOtp(email);
      setOtpEmail(email);
      if (result?.devCode) setDevCode(result.devCode);
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function authenticate(code) {
    setBusy(true);
    setError("");
    try {
      await verifyOtp(otpEmail, code);
      await refresh();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }
  async function logout() {
    await signOut().catch(() => undefined);
    setWorkspace(null);
    setAuthRequired(true);
  }
  async function toggleFavorite(photo) {
    const favorite = !photo.favorite;
    try {
      await setFavorite(eventId, photo.id, favorite);
      setPhotos((items) =>
        items.map((item) =>
          item.id === photo.id ? { ...item, favorite } : item,
        ),
      );
    } catch (requestError) {
      setError(requestError.message);
    }
  }
  async function toggleSharing(photo) {
    try {
      await setSharing(eventId, photo.id, photo.privacy !== "shared");
      await refreshPhotos();
    } catch (requestError) {
      setError(requestError.message);
    }
  }
  async function shareAllReadyPhotos() {
    const shareable = mine.filter(
      (photo) =>
        photo.privacy !== "shared" &&
        photo.status === "ready" &&
        photo.moderationState === "clear",
    );
    if (!eventId || !shareable.length || shareBusy) return;
    setShareBusy(true);
    setError("");
    try {
      await Promise.all(
        shareable.map((photo) => setSharing(eventId, photo.id, true)),
      );
      await refreshPhotos();
      setView("find");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setShareBusy(false);
    }
  }
  async function loadMore(scope) {
    if (!eventId || moreBusy) return;
    const requestedEvent = eventId;
    const isMine = scope === "mine";
    const nextPage = (isMine ? minePage : galleryPage) + 1;
    if (nextPage > (isMine ? minePages : galleryPages)) return;
    setMoreBusy(scope);
    try {
      const next = await loadGalleryPage(
        requestedEvent,
        isMine ? "mine" : "shared",
        nextPage,
      );
      if (galleryEventRef.current !== requestedEvent) return;
      const update = isMine ? setMine : setPhotos;
      update((current) => [
        ...current,
        ...next.photos.filter(
          (photo) => !current.some((item) => item.id === photo.id),
        ),
      ]);
      if (isMine) {
        setMinePage(next.pagination.page);
        setMinePages(next.pagination.pages);
      } else {
        setGalleryPage(next.pagination.page);
        setGalleryPages(next.pagination.pages);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setMoreBusy("");
    }
  }

  async function upload(files) {
    const selected = Array.from(files || []).slice(0, 20);
    if (!selected.length || !eventId || !uploadPolicy) return;
    const available = selected.slice(0, uploadPolicy.photosRemaining);
    if (available.length !== selected.length)
      setError(
        `Only ${uploadPolicy.photosRemaining} Guest upload slots remain.`,
      );
    const entries = available.map((file) => ({
      id: createUploadId(),
      file,
      name: file.name,
      progress: 0,
      status: "queued",
    }));
    setUploadQueue((current) => [...entries, ...current]);
    // Guest admission uses a short event lock to enforce the five-photo limit.
    // Initiate serially so parallel requests do not contend for that lock.
    for (const entry of entries) await runUpload(entry);
    await Promise.all([
      refreshPhotos(),
      loadGuestUploadPolicy(eventId).then(setUploadPolicy),
    ]);
  }
  async function runUpload(entry) {
    const controller = new AbortController();
    uploadControllers.current.set(entry.id, controller);
    const update = (values) =>
      setUploadQueue((items) =>
        items.map((item) =>
          item.id === entry.id ? { ...item, ...values } : item,
        ),
      );
    try {
      validateGuestFile(entry.file, uploadPolicy);
      update({ status: "uploading", error: "" });
      await uploadPhoto(eventId, entry.file, "guest", {
        clientUploadId: entry.id,
        signal: controller.signal,
        onProgress: (loaded, total) =>
          update({ progress: Math.round((loaded / total) * 100) }),
      });
      update({ status: "processing", progress: 100 });
    } catch (requestError) {
      update({
        status: requestError.name === "AbortError" ? "cancelled" : "failed",
        error: requestError.message,
      });
    } finally {
      uploadControllers.current.delete(entry.id);
    }
  }
  function cancelUpload(id) {
    uploadControllers.current.get(id)?.abort();
  }
  function retryUpload(entry) {
    const retry = {
      ...entry,
      id: createUploadId(),
      progress: 0,
      status: "queued",
      error: "",
    };
    setUploadQueue((items) =>
      items.map((item) => (item.id === entry.id ? retry : item)),
    );
    runUpload(retry);
  }
  async function retryProcessing(photo) {
    try {
      await retryPhotoProcessing(eventId, photo.id);
      await refreshPhotos();
    } catch (requestError) {
      setError(requestError.message);
    }
  }
  async function discardIncompleteUpload(photo) {
    try {
      await abortPhotoUpload(eventId, photo.id);
      await Promise.all([
        refreshPhotos(),
        loadGuestUploadPolicy(eventId).then(setUploadPolicy),
      ]);
    } catch (requestError) {
      setError(requestError.message);
    }
  }
  async function searchFace(file, consentConfirmed) {
    if (!file || !eventId) return;
    if (consentConfirmed !== true) {
      setError(
        "Consent is required before sending a selfie for event-only face search.",
      );
      return;
    }
    if (
      !["image/jpeg", "image/png"].includes(file.type) ||
      file.size > 5 * 1024 * 1024 ||
      !file.size
    ) {
      setError("Choose a JPG or PNG selfie smaller than 5 MiB.");
      return;
    }
    const requestedEventId = eventId;
    setBusy(true);
    setMatches([]);
    setFaceSearchCompleted(false);
    setError("");
    try {
      const results = await faceSearch(
        requestedEventId,
        await readDataUrl(file),
        true,
      );
      const enriched = await hydrateFaceMatches(
        requestedEventId,
        results,
        photos,
      );
      if (requestedEventId === currentFaceSearchEvent.current) {
        setMatches(enriched);
        setFaceSearchCompleted(true);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  if (loading)
    return <Status icon={LoaderCircle} spin title="Loading guest workspace" />;
  if (!workspace && authRequired)
    return (
      <JoinScreen
        access={access}
        busy={busy}
        error={error}
        otpEmail={otpEmail}
        initialCode={otpFill?.code || ""}
        fillCode={otpFill?.code || ""}
        devCode={devCode}
        sendCode={sendCode}
        verify={authenticate}
        reset={() => {
          setOtpEmail("");
          setError("");
          setDevCode("");
          setOtpFill(null);
        }}
      />
    );
  if (!workspace)
    return (
      <Status
        icon={Lock}
        title="Guest session required"
        message={error}
        action={refresh}
      />
    );
  const selectedEvent = workspace.events.find((event) => event.id === eventId);

  return (
    <main className="guest-app">
      <header className="guest-header">
        <div className="guest-brand">
          <span>
            <Image />
          </span>
          PhotoX
        </div>
        <select
          value={eventId}
          onChange={(event) => setEventId(event.target.value)}
        >
          <option value="">Select an event</option>
          {workspace.events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
        <button onClick={refresh} aria-label="Refresh">
          <RefreshCw size={18} />
        </button>
        <button onClick={logout} aria-label="Sign out">
          <LogOut size={18} />
        </button>
      </header>
      {error && <Notice error text={error} />}
      {workspace.events.length === 0 ? (
        <section className="guest-content">
          <div className="dynamic-status guest-no-invitation" role="status">
            <ScanLine />
            <h1>No event invitation found</h1>
            <p>
              This email is verified, but it is not linked to an event yet. Scan
              the event QR from your photographer or organizer to continue.
            </p>
            <a className="button button-dark" href="/">
              Return to PhotoX
            </a>
          </div>
        </section>
      ) : (
        <section className="guest-content">
          {view === "gallery" && (
            <Gallery
              event={selectedEvent}
              photos={photos}
              toggleFavorite={toggleFavorite}
              moreBusy={moreBusy === "shared"}
              hasMore={galleryPage < galleryPages}
              onLoadMore={() => loadMore("shared")}
            />
          )}
          {view === "find" && (
            <FaceSearch
              eventId={eventId}
              busy={busy}
              matches={matches}
              search={searchFace}
              searched={faceSearchCompleted}
              readiness={faceSearchReadiness}
              readinessLoading={faceSearchReadinessLoading}
              privateReadyCount={
                mine.filter(
                  (photo) =>
                    photo.privacy !== "shared" &&
                    photo.status === "ready" &&
                    photo.moderationState === "clear",
                ).length
              }
              openMyPhotos={() => setView("mine")}
            />
          )}
          {view === "mine" && (
            <MyPhotos
              eventId={eventId}
              photos={mine}
              hasMore={minePage < minePages}
              moreBusy={moreBusy === "mine"}
              onLoadMore={() => loadMore("mine")}
              policy={uploadPolicy}
              queue={uploadQueue}
              upload={upload}
              cancelUpload={cancelUpload}
              retryUpload={retryUpload}
              retryProcessing={retryProcessing}
              discardIncompleteUpload={discardIncompleteUpload}
              toggleSharing={toggleSharing}
              shareAllReadyPhotos={shareAllReadyPhotos}
              shareBusy={shareBusy}
            />
          )}
          {view === "profile" && (
            <Profile user={workspace.user} events={workspace.events} />
          )}
        </section>
      )}
      {workspace.events.length > 0 && (
        <nav className="guest-nav" aria-label="Guest sections">
          {nav.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              aria-current={view === id ? "page" : undefined}
              onClick={() => setView(id)}
            >
              <Icon size={19} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      )}
    </main>
  );
}

function JoinScreen({
  access,
  busy,
  error,
  otpEmail,
  initialCode,
  fillCode,
  devCode,
  sendCode,
  verify,
  reset,
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(initialCode || fillCode || "");
  const event = access?.event;
  const awaitingCode = Boolean(otpEmail);

  useEffect(() => {
    if (fillCode) {
      setCode(fillCode);
    }
  }, [fillCode]);

  return (
    <main className="join-page">
      <section className="join-event">
        <span className="join-icon">
          <ScanLine />
        </span>
        <p className="eyebrow">QR invitation</p>
        <h1>{event?.name || "Join your event"}</h1>
        <p>
          {event
            ? `${formatDate(event.startsAt)} · ${event.venue?.name || event.venue?.city || "Venue to be announced"}`
            : "Sign in to open the private event gallery."}
        </p>
        {event?.studio?.name && <small>Hosted by {event.studio.name}</small>}
      </section>
      <form
        className="join-form"
        onSubmit={async (formEvent) => {
          formEvent.preventDefault();
          if (awaitingCode) await verify(code);
          else await sendCode(email);
        }}
      >
        <p className="eyebrow">Guest sign in</p>
        <h2>{awaitingCode ? "Check your email" : "Continue to the gallery"}</h2>
        <p>
          {awaitingCode
            ? `Enter the one-time code sent to ${otpEmail}.`
            : "Enter your email and we’ll send a secure one-time code. No password or social login is required."}
        </p>
        {error && <Notice error text={error} />}
        {fillCode && awaitingCode && (
          <button
            type="button"
            className="dev-otp-badge"
            onClick={() => setCode(fillCode)}
            title="Tap to fill OTP code from magic link"
            style={{
              cursor: "pointer",
              background: "#eef8f6",
              border: "1px dashed #54b7ae",
              borderRadius: "10px",
              color: "#176b63",
              padding: "10px 14px",
              marginTop: "12px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              Magic link OTP: <strong>{fillCode}</strong>
            </span>
            <span
              style={{ fontSize: "11px", fontWeight: "bold", color: "#176b63" }}
            >
              Tap to fill
            </span>
          </button>
        )}
        {showDevOtpFill(devCode) && !fillCode && awaitingCode && (
          <button
            type="button"
            className="guest-login-dev-code"
            onClick={() => {
              setCode(devCode);
              setError(null);
            }}
            title="Tap to fill the same OTP emailed by SES (testing only)"
            style={{
              cursor: "pointer",
              background: "#eef8f6",
              border: "1px dashed #54b7ae",
              borderRadius: "10px",
              color: "#176b63",
              padding: "10px 14px",
              marginTop: "12px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>
              Testing OTP: <strong>{devCode}</strong>
            </span>
            <span
              style={{ fontSize: "11px", fontWeight: "bold", color: "#176b63" }}
            >
              {code === devCode ? "✓ Filled" : "Tap to fill"}
            </span>
          </button>
        )}
        {awaitingCode ? (
          <label>
            6-digit OTP
            <input
              required
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength="6"
              onChange={(item) =>
                setCode(item.target.value.replace(/\D/g, "").slice(0, 6))
              }
            />
          </label>
        ) : (
          <label>
            Email
            <input
              required
              type="email"
              value={email}
              autoComplete="email"
              onChange={(item) => setEmail(item.target.value)}
            />
          </label>
        )}
        <button
          className="button button-dark"
          disabled={busy || (awaitingCode && code.length !== 6)}
        >
          {busy ? (
            <LoaderCircle className="spin" size={18} />
          ) : (
            <Lock size={18} />
          )}
          {awaitingCode ? "Verify & join event" : "Send OTP"}
        </button>
        {awaitingCode && (
          <button className="join-switch" type="button" onClick={reset}>
            Use a different email
          </button>
        )}
      </form>
    </main>
  );
}
function Gallery({
  event,
  photos,
  toggleFavorite,
  hasMore,
  moreBusy,
  onLoadMore,
}) {
  return (
    <section className="gallery-section">
      <div className="section-head">
        <div>
          <p className="eyebrow">{event?.name || "Event gallery"}</p>
          <h1>Shared photographs</h1>
          <p className="muted">
            Only media authorized and signed by the backend appears here.
          </p>
        </div>
      </div>
      {photos.length ? (
        <div className="gallery-grid">
          {photos.map((photo) => (
            <article className="photo-card" key={photo.id}>
              {photo.mediaUrl ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={photo.mediaUrl}
                  alt={
                    photo.caption ||
                    photo.originalFilename ||
                    "Shared event photo"
                  }
                />
              ) : (
                <div className="media-empty">
                  <Images size={32} />
                </div>
              )}
              <button
                className="like"
                onClick={() => toggleFavorite(photo)}
                aria-label={
                  photo.favorite ? "Remove favourite" : "Add favourite"
                }
                aria-pressed={Boolean(photo.favorite)}
              >
                <Heart fill={photo.favorite ? "currentColor" : "none"} />
              </button>
              <div className="photo-info">
                <strong>{photo.originalFilename}</strong>
                <small>
                  {photo.kind} · {photo.status}
                </small>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          text={event ? "No shared photos were returned." : "Select an event."}
        />
      )}
      {hasMore && (
        <button
          type="button"
          className="px-load-more"
          disabled={moreBusy}
          onClick={onLoadMore}
        >
          {moreBusy ? "Loading photographs…" : "Load more shared photographs"}
        </button>
      )}
    </section>
  );
}
export function FaceSearch({
  eventId,
  busy,
  matches,
  search,
  searched = false,
  readiness = emptyFaceSearchReadiness,
  readinessLoading = false,
  privateReadyCount = 0,
  openMyPhotos,
}) {
  const [consent, setConsent] = useState(false);
  const galleryReady =
    readiness.ready &&
    readiness.indexedPhotoCount > 0 &&
    readiness.pendingPhotoCount === 0;
  useEffect(() => {
    setConsent(false);
  }, [eventId]);
  return (
    <section className="inner-page">
      <div className="section-head">
        <div>
          <p className="eyebrow">Event-only face search</p>
          <h1>Find my photographs</h1>
          <p className="muted">
            We use your selfie temporarily to find matching photographs from
            this event's explicitly shared gallery.
          </p>
        </div>
      </div>
      <label className="face-consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>
          I consent to processing my selfie to search this event's shared
          photos. My selfie is not added to the gallery.
        </span>
      </label>
      {readinessLoading ? (
        <div className="face-search-readiness" role="status">
          <strong>Checking face-search readiness…</strong>
        </div>
      ) : !readiness.sharedPhotoCount ? (
        <div className="face-search-readiness" role="status">
          <strong>No shared photographs are ready for face search.</strong>
          <p>
            Guest uploads stay private until you explicitly share them from My
            photos. Rekognition cannot index private photographs.
          </p>
          {privateReadyCount > 0 && openMyPhotos && (
            <button
              type="button"
              className="button button-dark"
              onClick={openMyPhotos}
            >
              <Share2 size={16} /> Review and share {privateReadyCount}{" "}
              {privateReadyCount === 1 ? "photo" : "photos"}
            </button>
          )}
        </div>
      ) : !galleryReady && readiness.pendingPhotoCount > 0 ? (
        <div className="face-search-readiness" role="status">
          <strong>Preparing shared photographs for face search…</strong>
          <p>
            {readiness.indexedPhotoCount > 0
              ? `${readiness.indexedPhotoCount} ready; `
              : ""}
            {readiness.pendingPhotoCount}{" "}
            {readiness.pendingPhotoCount === 1
              ? "photograph is"
              : "photographs are"}{" "}
            still being indexed. Search will enable automatically when indexing
            finishes.
          </p>
        </div>
      ) : !galleryReady ? (
        <div className="face-search-readiness" role="status">
          <strong>No searchable faces are indexed yet.</strong>
          <p>
            The shared photographs were processed, but Rekognition has not
            produced a searchable face. Try another event photograph.
          </p>
        </div>
      ) : null}
      <label
        className={`real-upload ${!consent || !galleryReady || readinessLoading ? "face-upload-disabled" : ""}`}
      >
        <Search size={28} />
        <span>
          {busy ? "Searching…" : "Choose a JPG or PNG selfie (maximum 5 MiB)"}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png"
          disabled={
            !eventId || busy || !consent || !galleryReady || readinessLoading
          }
          onChange={(event) => {
            search(event.target.files?.[0], consent);
            event.target.value = "";
          }}
        />
      </label>
      {matches.length ? (
        <div className="match-grid">
          {matches.map((match) => (
            <article key={match.photo.id}>
              {match.mediaUrl ? (
                <img
                  loading="lazy"
                  decoding="async"
                  src={match.mediaUrl}
                  alt={
                    match.photo.originalFilename || "Matching event photograph"
                  }
                />
              ) : (
                <div className="media-empty">
                  <Image />
                </div>
              )}
              <strong>{Number(match.similarity || 0).toFixed(1)}% match</strong>
            </article>
          ))}
        </div>
      ) : searched ? (
        <Empty text="Search completed. No matching photographs were found. Try a well-lit, front-facing selfie after shared photos finish indexing." />
      ) : galleryReady ? (
        <Empty text="Choose a well-lit, front-facing selfie to search the shared event gallery." />
      ) : null}
    </section>
  );
}
function MyPhotos({
  eventId,
  photos,
  hasMore,
  moreBusy,
  onLoadMore,
  policy,
  queue,
  upload,
  cancelUpload,
  retryUpload,
  retryProcessing,
  discardIncompleteUpload,
  toggleSharing,
  shareAllReadyPhotos,
  shareBusy = false,
}) {
  const canUpload = Boolean(
    eventId && policy?.allowed && policy.photosRemaining > 0,
  );
  const privateReadyCount = photos.filter(
    (photo) =>
      photo.privacy !== "shared" &&
      photo.status === "ready" &&
      photo.moderationState === "clear",
  ).length;
  return (
    <section className="inner-page">
      <div className="section-head">
        <div>
          <p className="eyebrow">Your event uploads</p>
          <h1>My photos</h1>
          <p className="muted">
            New guest uploads remain private until explicitly shared.
          </p>
          {policy && (
            <small className="upload-guidance">
              JPG, PNG, HEIC or HEIF · up to{" "}
              {Math.round(policy.maxFileSizeBytes / 1048576)} MiB each ·{" "}
              {policy.photosRemaining} remaining
            </small>
          )}
        </div>
        <label className="button button-primary" aria-disabled={!canUpload}>
          <Upload size={17} />
          Add photos
          <input
            hidden
            multiple
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
            disabled={!canUpload}
            onChange={(event) => {
              upload(event.target.files);
              event.target.value = "";
            }}
          />
        </label>
      </div>
      {policy && !policy.allowed && (
        <Notice
          error
          text={
            policy.reason?.message || "Uploads are unavailable for this event."
          }
        />
      )}
      {privateReadyCount > 0 && (
        <div className="share-ready-callout">
          <div>
            <strong>
              {privateReadyCount} ready{" "}
              {privateReadyCount === 1 ? "photo is" : "photos are"} still
              private
            </strong>
            <p>
              Share them with this event to add them to the gallery and AWS
              face-search index.
            </p>
          </div>
          <button
            type="button"
            className="button button-dark"
            disabled={shareBusy}
            onClick={shareAllReadyPhotos}
          >
            {shareBusy ? (
              <LoaderCircle className="spin" size={16} />
            ) : (
              <Share2 size={16} />
            )}
            {shareBusy ? "Sharing..." : "Share all ready photos"}
          </button>
        </div>
      )}
      {queue.length > 0 && (
        <div className="upload-queue" aria-live="polite">
          {queue.map((item) => (
            <div className="upload-row" key={item.id}>
              <div>
                <strong>{item.name}</strong>
                <small>{item.error || item.status}</small>
              </div>
              <progress
                max="100"
                value={item.progress}
                aria-label={`${item.name} upload progress`}
              />
              {["queued", "uploading"].includes(item.status) && (
                <button
                  onClick={() => cancelUpload(item.id)}
                  aria-label={`Cancel ${item.name}`}
                >
                  <X size={16} />
                </button>
              )}
              {["failed", "cancelled"].includes(item.status) && (
                <button
                  onClick={() => retryUpload(item)}
                  aria-label={`Retry ${item.name}`}
                >
                  <RotateCcw size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {photos.length ? (
        <div className="gallery-grid">
          {photos.map((photo) => {
            const shareable =
              photo.status === "ready" && photo.moderationState === "clear";
            return (
              <article className="photo-card" key={photo.id}>
                {photo.mediaUrl ? (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={photo.mediaUrl}
                    alt={photo.originalFilename || "Your uploaded photo"}
                  />
                ) : (
                  <div className="media-empty">
                    <Image />
                    {photo.status === "uploading" && (
                      <span>Upload incomplete</span>
                    )}
                  </div>
                )}
                <div className="photo-info">
                  <strong>{photo.originalFilename}</strong>
                  <small>
                    {photo.privacy} · {photo.status}
                  </small>
                  {photo.status === "uploading" ? (
                    <button
                      className="button button-light"
                      onClick={() => discardIncompleteUpload(photo)}
                    >
                      <X size={15} /> Remove incomplete upload
                    </button>
                  ) : photo.status === "failed" &&
                    photo.failure?.recoverable ? (
                    <button
                      className="button button-light"
                      onClick={() => retryProcessing(photo)}
                    >
                      <RotateCcw size={15} /> Retry processing
                    </button>
                  ) : (
                    <button
                      className="button button-light"
                      disabled={photo.privacy !== "shared" && !shareable}
                      onClick={() => toggleSharing(photo)}
                    >
                      {photo.privacy === "shared" ? (
                        <>
                          <Lock size={15} /> Make private
                        </>
                      ) : (
                        <>
                          <Share2 size={15} /> Share
                        </>
                      )}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <Empty
          text={
            eventId ? "You have no uploads for this event." : "Select an event."
          }
        />
      )}
      {hasMore && (
        <button
          type="button"
          className="px-load-more"
          disabled={moreBusy}
          onClick={onLoadMore}
        >
          {moreBusy
            ? "Loading your photographs…"
            : "Load more of my photographs"}
        </button>
      )}
    </section>
  );
}
function Profile({ user, events }) {
  return (
    <section className="inner-page profile-page">
      <div className="profile-card">
        <div className="profile-avatar">
          {initials(user.displayName || user.email)}
        </div>
        <p className="eyebrow">Authenticated guest</p>
        <h1>{user.displayName || "Guest"}</h1>
        <p>{user.email}</p>
        <div className="profile-detail">
          <span>
            Accessible events<strong>{events.length}</strong>
          </span>
          <span>
            Account status<strong>{user.status}</strong>
          </span>
        </div>
      </div>
    </section>
  );
}
function Empty({ text }) {
  return <div className="dynamic-empty">{text}</div>;
}
function Notice({ text, error }) {
  return <div className={`dynamic-notice ${error ? "error" : ""}`}>{text}</div>;
}
function Status({ icon: Icon, spin, title, message, action }) {
  return (
    <main className="dynamic-status">
      <Icon className={spin ? "spin" : ""} />
      <h1>{title}</h1>
      {message && <p>{message}</p>}
      {action && (
        <button className="button button-dark" onClick={action}>
          Retry
        </button>
      )}
    </main>
  );
}
function initials(value) {
  return String(value || "G")
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
function readDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read selfie"));
    reader.readAsDataURL(file);
  });
}
function formatDate(value) {
  if (!value) return "Date to be announced";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "long" }).format(
    new Date(value),
  );
}
function createUploadId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default App;
